import React, { useEffect, useState } from 'react';
import {
  CampaignFormSection,
  CampaignTypes,
  CAMPAIGN_FORM_CARDS,
  ChannelType,
  MAX_ALLOWED_DESCRIPTION_LENGTH,
  MAX_ALLOWED_NAME_LENGTH,
} from '../../enums/schedulingConstant';
import { Col, Spacer, View } from '@amzn/stencil-react-components/layout';
import { FormWrapper } from '@amzn/stencil-react-components/form';
import { InputWrapper, Input } from '@amzn/stencil-react-components/input';
import { Radio } from '@amzn/stencil-react-components/radio';
import { FormattedMessage } from 'react-intl';
import { Label } from '@amzn/stencil-react-components/text';
import { CardWithRadio } from '../atoms/CardWithRadio';
import { useDispatch, useSelector } from 'react-redux';
import { updateCampaignForm } from './../../actions/createScheduleAction';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import { CHANNEL_TYPE_TOOLTIP_TEXT } from '../../constants/messages';

import { IconQuestionCircleSmall } from '@amzn/stencil-react-icons';

const IconWithTooltip = withTooltip()(IconQuestionCircleSmall);
export const CampaignSection = () => {
  const dispatch = useDispatch();
  const campaignDetails = useSelector(store => store.campaign.campaignFormState?.campaignDetails);
  const isSubmitSuccess = useSelector(store => store.campaign.campaignFormState.isSubmitSuccess);
  const campaignFormState = useSelector(store => store.campaign.campaignFormState);
  const [campaignName = '', setCampaignName] = useState(campaignDetails.campaignName);
  const [campaignDescription = '', setCampaignDescription] = useState(
    campaignDetails.campaignDescription,
  );
  const [channelType = ChannelType.EMAIL, setChannelType] = useState(campaignDetails.channelType);
  const hasChannelTypeError = !channelType;
  const [campaignType, setCampaignType] = useState(CampaignTypes.STANDARD);
  const campaignNameError = false;
  const campaignNameFooterText = 'Campaign name must be between 1 and 64 characters';

  const campaignDescriptionError = false;
  const campaignDescriptionFooterText = 'Campaign description must be between 1 and 100 characters';

  useEffect(() => {
    dispatch(updateCampaignForm(CampaignFormSection.CAMPAIGN, { channelType, campaignType }));
  }, []);

  const cardList = CAMPAIGN_FORM_CARDS;

  const onCampaignTypeChange = e => {
    setCampaignType(e);
    dispatch(updateCampaignForm(CampaignFormSection.CAMPAIGN, { campaignType }));
  };
  return (
    <>
      <Col gridGap="S200">
        <View>
          <InputWrapper
            labelText="Campaign Name"
            fontSize={'T400'}
            id="campaign-name-wrapper"
            error={campaignNameError}
            footer={campaignNameFooterText}
          >
            {inputProps => (
              <Input
                {...inputProps}
                data-testid="campaign-name-input"
                error={campaignNameError}
                maxLength={MAX_ALLOWED_NAME_LENGTH}
                value={campaignName}
                disabled={isSubmitSuccess}
                onChange={e => {
                  setCampaignName(e.target.value);
                  dispatch(
                    updateCampaignForm(CampaignFormSection.CAMPAIGN, {
                      campaignName: e.target.value ?? '',
                    }),
                  );
                }}
              />
            )}
          </InputWrapper>
        </View>
        <Spacer height={20} />
        <View>
          <InputWrapper
            labelText="Campaign Description"
            id="campaign-description-wrapper"
            error={campaignDescriptionError}
            footer={campaignDescriptionFooterText}
          >
            {inputProps => (
              <Input
                {...inputProps}
                data-testid="campaign-description-input"
                error={campaignDescriptionError}
                value={campaignDescription}
                disabled={isSubmitSuccess}
                maxLength={MAX_ALLOWED_DESCRIPTION_LENGTH}
                onChange={e => {
                  setCampaignDescription(e.target.value);
                  dispatch(
                    updateCampaignForm(CampaignFormSection.CAMPAIGN, {
                      campaignDescription: e.target.value ?? '',
                    }),
                  );
                }}
              />
            )}
          </InputWrapper>
        </View>
      </Col>
      <Spacer height={30} />
      <Col gridGap="S200">
        <Label fontSize={'T400'} color="primaryLabelText">
          <FormattedMessage id="app.campaign.label.campaignType" defaultMessage="Channel Type" />{' '}
        </Label>
        <Col display="flex" style={{ flexDirection: 'row' }}>
          {cardList.map((card, index) => {
            return (
              <>
                <CardWithRadio
                  width={card.width}
                  label={card.label}
                  description={card.description}
                  radioName={card.radioName}
                  radioValue={card.radioValue}
                  onChange={() => onCampaignTypeChange(card.radioValue)}
                  isDisabled={card.isDisabled || isSubmitSuccess}
                  isChecked={card.radioValue === campaignType}
                  key={index}
                />
                <Spacer width={30} />
              </>
            );
          })}
        </Col>
      </Col>
      <Spacer height={30} />
      <Col gridGap="S200">
        <Label fontSize={'T400'} color="primaryLabelText">
          <FormattedMessage id="app.campaign.label.channelType" defaultMessage="Channel type" />
          <IconWithTooltip
            title="Channel Type"
            tooltipText={CHANNEL_TYPE_TOOLTIP_TEXT}
            color="primary70"
          />
        </Label>
        <Spacer height="S300" />
        <FormWrapper>
          <View display="flex" alignItems="center">
            <Radio
              id="channel-type-email"
              data-testid="channel-type-email-option"
              name="output-format"
              value={ChannelType.EMAIL}
              checked={channelType === ChannelType.EMAIL}
              error={hasChannelTypeError}
              disabled={isSubmitSuccess}
              onChange={() => {
                setChannelType(ChannelType.EMAIL);
                dispatch(updateCampaignForm(CampaignFormSection.CAMPAIGN, { channelType }));
              }}
              required
            />
            <Spacer width={30} />
            <Label fontSize={'T300'} htmlFor="channel-type-email">
              EMAIL
            </Label>
          </View>
          {/* TODO: Enable when these channels are functional */}
          {/* <View display="flex" alignItems="center">
                  <Radio
                    id="channel-type-email"
                    name="output-format"
                    checked={channelType === ChannelType.SLACK}
                    error={hasChannelTypeError}
                    onChange={() => setChannelType(ChannelType.SLACK)}
                    required
                  />
                  <Spacer width={30} />
                  <Label fontSize={'T300'} htmlFor="channel-type-email">
                    Slack
                  </Label>
                </View>
                <View display="flex" alignItems="center">
                  <Radio
                    id="channel-type-email"
                    name="output-format"
                    checked={channelType === ChannelType.SMS}
                    error={hasChannelTypeError}
                    onChange={() => setChannelType(ChannelType.SMS)}
                    required
                  />
                  <Spacer width={30} />
                  <Label fontSize={'T300'} htmlFor="channel-type-email">
                    SMS
                  </Label>
                </View>
                <View display="flex" alignItems="center">
                  <Radio
                    id="channel-type-email"
                    name="output-format"
                    checked={channelType === ChannelType.CUSTOM}
                    error={hasChannelTypeError}
                    onChange={() => setChannelType(ChannelType.CUSTOM)}
                    required
                  />
                  <Spacer width={30} />
                  <Label fontSize={'T300'} htmlFor="channel-type-email">
                    Custom
                  </Label>
                </View> */}
        </FormWrapper>
      </Col>
    </>
  );
};
